import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';

import { Button } from '../button';
import { modalDefaultStyles, Popup, PopupProps } from '../popup';
import { Typography } from '../typography';

const WARNING_DURATION_SECONDS = 5 * 60; // 5 minutes

interface IdleWarningPopupProps extends PopupProps {
    onExtendSession: () => void;
    onTimeout: () => void;
}

export const IdleWarningPopup: FC<IdleWarningPopupProps> = ({ onExtendSession, onTimeout, ...props }) => {
    const { t } = useTranslation();
    const [remainingSeconds, setRemainingSeconds] = useState<number>(WARNING_DURATION_SECONDS);

    useEffect(() => {
        if (!props.show) {
            setRemainingSeconds(WARNING_DURATION_SECONDS);
            return;
        }

        if (remainingSeconds === 0) {
            onTimeout();
            return;
        }

        const timer = setTimeout(() => {
            setRemainingSeconds((s) => s - 1);
        }, 1000);

        return () => clearTimeout(timer);
    }, [onTimeout, props.show, remainingSeconds]);

    const remainingTimeString = useMemo(() => {
        const tmpDate = new Date(0);
        tmpDate.setSeconds(remainingSeconds);
        return tmpDate.toISOString().substr(14, 5);
    }, [remainingSeconds]);

    return (
        <Popup {...props}>
            <View style={modalDefaultStyles.header}>
                <Typography variant="h4">{t('shared:popups.idleWarning.title', 'Session About to Expire')}</Typography>
            </View>

            <View style={modalDefaultStyles.content}>
                <Typography variant="lead">
                    {t(
                        'shared:popups.idleWarning.message',
                        'Your session will expire in {{time}} due to inactivity. Click "Extend Session" to stay logged in.',
                        { time: remainingTimeString }
                    )}
                </Typography>
            </View>

            <View style={[modalDefaultStyles.footer, styles.footer]}>
                <Button
                    title={t('shared:popups.idleWarning.extendSession', 'Extend Session')}
                    onPress={onExtendSession}
                />
                <Button
                    title={t('shared:popups.idleWarning.logoutNow', 'Logout Now')}
                    variant="secondary"
                    onPress={onTimeout}
                />
            </View>
        </Popup>
    );
};

const styles = StyleSheet.create({
    footer: {
        gap: 8,
    },
});
